import { apiFetch } from "./client";
import type { Page } from "./admin";

export type ArticleStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export type ArticleSummary = {
  id: string;
  slug: string;
  coverMinioKey: string | null;
  categoryId: string;
  categorySlug: string;
  status: ArticleStatus;
  publishedAt: string | null;
  title: string;
  excerpt: string | null;
};

export type ArticleTranslation = {
  title: string;
  excerpt: string | null;
  bodyHtml: string | null;
};

export type ArticleDetail = {
  id: string;
  slug: string;
  categoryId: string;
  categorySlug: string;
  coverMinioKey: string | null;
  status: ArticleStatus;
  visibleToRoles: string[];
  authorId: string | null;
  publishedAt: string | null;
  scheduledPublishAt: string | null;
  translations: Record<string, ArticleTranslation>;
};

const q = (params: Record<string, string | number | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") usp.set(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const newsApi = {
  list: (params: { categoryId?: string; locale?: string; page?: number; size?: number } = {}) =>
    apiFetch<Page<ArticleSummary>>(`/news${q({ ...params, sort: "publishedAt,desc" })}`),

  latest: (limit = 3, locale = "en") =>
    apiFetch<ArticleSummary[]>(`/news/latest${q({ limit, locale })}`),

  bySlug: (slug: string) => apiFetch<ArticleDetail>(`/news/${encodeURIComponent(slug)}`),

  // Admin
  get: (id: string) => apiFetch<ArticleDetail>(`/admin/news/${id}`),
  create: (body: {
    slug: string;
    categoryId: string;
    coverMinioKey?: string;
    visibleToRoles?: string[];
    scheduledPublishAt?: string;
    translations?: Record<string, ArticleTranslation>;
  }) => apiFetch<ArticleDetail>("/admin/news", { method: "POST", body }),
  update: (id: string, body: Partial<ArticleDetail>) =>
    apiFetch<ArticleDetail>(`/admin/news/${id}`, { method: "PUT", body }),
  publish: (id: string) => apiFetch<void>(`/admin/news/${id}/publish`, { method: "POST" }),
  archive: (id: string) => apiFetch<void>(`/admin/news/${id}/archive`, { method: "POST" }),
  delete: (id: string) => apiFetch<void>(`/admin/news/${id}`, { method: "DELETE" }),
  wpSync: () =>
    apiFetch<{ created: number; updated: number; skipped: number; errors: string[] }>(
      "/admin/news/wp-sync",
      { method: "POST" },
    ),
};
